<template>
  <div>
    <div class="crm-flowbar">
      <router-link class="crm-back" to="/crm-salon">← Accueil</router-link>
    </div>
    <p class="crm-kicker">Parcours stand</p>
    <h1 class="crm-title">{{ isNew ? 'Nouveau contact' : 'Visite rapide' }}</h1>
    <p class="crm-hint">Formulaire court — pas de qualification complète obligatoire.</p>

    <div class="crm-card">
      <div class="crm-field">
        <label class="crm-label">Prénom</label>
        <input class="crm-input" v-model="form.prenom" />
      </div>
      <div class="crm-field">
        <label class="crm-label">Nom</label>
        <input class="crm-input" v-model="form.nom" />
      </div>
      <div class="crm-field crm-suggest-wrap">
        <label class="crm-label">Cabinet / société</label>
        <input
          class="crm-input"
          v-model="form.societe"
          autocomplete="off"
          placeholder="Nom du cabinet — suggestions clients"
          @input="onSocieteInput"
          @focus="onSocieteFocus"
          @blur="onSocieteBlur"
        />
        <div v-if="clientSuggest.length" class="crm-suggest">
          <p class="crm-suggest-kicker">Clients déjà en base</p>
          <button
            v-for="hit in clientSuggest"
            :key="hit.id"
            type="button"
            class="crm-suggest-item"
            @mousedown.prevent="pickClient(hit)"
          >
            <span class="crm-row-title">{{ hit.societe || display(hit) }}</span>
            <span class="crm-row-meta">{{ clientMeta(hit) }}</span>
          </button>
        </div>
      </div>
      <div class="crm-grid-2">
        <div class="crm-field">
          <label class="crm-label">Téléphone</label>
          <input class="crm-input" v-model="form.telephone" />
        </div>
        <div class="crm-field">
          <label class="crm-label">Email</label>
          <input class="crm-input" v-model="form.email" />
        </div>
      </div>
      <div class="crm-grid-2">
        <div class="crm-field">
          <label class="crm-label">N° ORIAS</label>
          <input class="crm-input" v-model="form.orias" placeholder="ex. 07001234" />
        </div>
        <div class="crm-field">
          <label class="crm-label">Ville</label>
          <input class="crm-input" v-model="form.ville" placeholder="ex. Quimper" />
        </div>
      </div>
      <div class="crm-field">
        <label class="crm-label">Salon</label>
        <input class="crm-input" v-model="salon" />
      </div>
      <div class="crm-field">
        <label class="crm-label">Type de passage</label>
        <button
          v-for="p in passages"
          :key="p.id"
          type="button"
          class="crm-choice"
          :class="{ on: passage === p.id }"
          @click="passage = p.id"
        >{{ p.label }}</button>
      </div>
      <div class="crm-field" style="margin-bottom:0">
        <label class="crm-label">À retenir pour la suite (interne)</label>
        <textarea class="crm-textarea" v-model="noteInterne" placeholder="Commentaire interne — jamais envoyé au contact"></textarea>
      </div>
    </div>

    <div class="crm-btn-stack">
      <button type="button" class="crm-btn crm-btn-primary crm-btn-lg" :disabled="saving" @click="saveVisit">
        {{ saving ? 'Enregistrement…' : 'Noter la visite' }}
      </button>
      <button type="button" class="crm-btn crm-btn-navy crm-btn-lg" @click="goQualify">Qualification complète</button>
      <button v-if="prospectId" type="button" class="crm-btn crm-btn-outline" @click="goFiche">Ouvrir la fiche</button>
    </div>
    <p v-if="msg" class="crm-hint" style="margin-top:12px">{{ msg }}</p>
  </div>
</template>

<script>
import { PASSAGES, displayName, withFrom } from './crmSalonUtils'
import { invalidateJourneeCache } from './crmSalonCache'

export default {
  name: 'CrmSalonVisite',
  data() {
    return {
      passages: PASSAGES,
      isNew: this.$route.query.new === '1',
      prospectId: this.$route.query.id || null,
      fromUser: null,
      form: {
        prenom: '',
        nom: '',
        societe: '',
        telephone: '',
        email: '',
        orias: '',
        ville: ''
      },
      salon: '',
      salonEventId: null,
      passage: 'visite',
      noteInterne: '',
      saving: false,
      msg: '',
      startedAt: Date.now(),
      known: false,
      origin: 'nouveau',
      clientSuggest: [],
      suggestTimer: null
    }
  },
  async mounted() {
    await this.loadCurrentSalon()
    if (this.prospectId) {
      await this.loadFiche(this.prospectId)
    } else {
      this.applyQueryPrefill()
    }
  },
  methods: {
    display: displayName,
    clientMeta(hit) {
      const name = [hit.prenom, hit.nom].filter(Boolean).join(' ')
      return [name, hit.email || hit.ville || hit.orias].filter(Boolean).join(' · ') || 'Client'
    },
    onSocieteInput() {
      clearTimeout(this.suggestTimer)
      this.suggestTimer = setTimeout(() => this.searchClients(), 280)
    },
    onSocieteFocus() {
      if ((this.form.societe || '').trim().length >= 2) this.searchClients()
    },
    onSocieteBlur() {},
    async searchClients() {
      const q = (this.form.societe || '').trim()
      if (q.length < 2) {
        this.clientSuggest = []
        return
      }
      try {
        this.clientSuggest = (await this.fetchClientSuggestions(q)).slice(0, 8)
      } catch (e) {
        this.clientSuggest = []
      }
    },
    async fetchClientSuggestions(q) {
      const needle = q.toLowerCase()
      let hits = []
      try {
        const { data } = await this.$apollo.query({
          client: 'AUTH',
          query: require('src/graphql/auth/crmSalon/crmSalonSearch.gql'),
          variables: { q },
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        })
        hits = (data && data.crmSalonSearch) || []
      } catch (e) {
        return []
      }
      const seen = {}
      const out = []
      hits
        .filter((h) => this.matchesSociete(h, needle))
        .sort((a, b) => this.clientRank(b) - this.clientRank(a))
        .forEach((h) => {
          const key = String(h.utilisateur_id || '') + '|' + String(h.email || h.societe || h.id).toLowerCase()
          if (seen[key]) return
          seen[key] = true
          out.push(h)
        })
      return out
    },
    matchesSociete(hit, needle) {
      const soc = String(hit.societe || '').toLowerCase()
      if (soc.indexOf(needle) !== -1) return true
      return hit.source === 'utilisateur'
    },
    clientRank(hit) {
      if (hit.source === 'utilisateur') return 2
      if (hit.utilisateur_id) return 1
      return 0
    },
    pickClient(hit) {
      this.form.societe = hit.societe || this.form.societe
      this.form.prenom = hit.prenom || this.form.prenom
      this.form.nom = hit.nom || this.form.nom
      this.form.email = hit.email || this.form.email
      this.form.telephone = hit.telephone || this.form.telephone
      this.form.orias = hit.orias || this.form.orias
      this.form.ville = hit.ville || this.form.ville
      this.known = true
      this.origin = hit.relation === 'client' ? 'actif' : this.origin
      if (hit.source === 'utilisateur' || String(hit.id).indexOf('u-') === 0) {
        this.fromUser = hit.utilisateur_id
        this.prospectId = hit.prospect_id || null
      } else if (hit.prospect_id || hit.id) {
        this.prospectId = hit.prospect_id || hit.id
        this.fromUser = hit.utilisateur_id || this.fromUser
      }
      this.clientSuggest = []
    },
    async loadCurrentSalon() {
      try {
        const { data } = await this.$apollo.query({
          client: 'AUTH',
          query: require('src/graphql/auth/crmSalon/crmSalonCurrentEvent.gql'),
          fetchPolicy: 'network-only'
        })
        const e = data.crmSalonCurrentEvent
        if (e) {
          this.salonEventId = e.id
          this.salon = e.name + (e.city ? ' · ' + e.city : '')
        } else {
          this.salon = 'Salon'
        }
      } catch (e) {
        this.salon = this.salon || 'Salon'
      }
    },
    applyQueryPrefill() {
      const q = this.$route.query || {}
      if (q.prenom) this.form.prenom = q.prenom
      if (q.nom) this.form.nom = q.nom
      if (q.societe) this.form.societe = q.societe
      if (q.tel) this.form.telephone = q.tel
      if (q.email) this.form.email = q.email
      if (q.orias) this.form.orias = q.orias
      if (q.ville) this.form.ville = q.ville
    },
    async loadFiche(id) {
      try {
        const { data } = await this.$apollo.query({
          client: 'AUTH',
          query: require('src/graphql/auth/crmSalon/crmSalonFiche.gql'),
          variables: { id: String(id) },
          fetchPolicy: 'network-only'
        })
        const f = data.crmSalonFiche
        if (!f) {
          this.applyQueryPrefill()
          return
        }
        this.form = {
          prenom: f.prenom || this.$route.query.prenom || '',
          nom: f.nom || this.$route.query.nom || '',
          societe: f.societe || this.$route.query.societe || '',
          telephone: f.telephone || this.$route.query.tel || '',
          email: f.email || this.$route.query.email || '',
          orias: f.orias || this.$route.query.orias || '',
          ville: f.ville || this.$route.query.ville || ''
        }
        this.known = f.source === 'utilisateur' || !!f.utilisateur_id || f.relation === 'client'
        this.origin = f.origin || (f.ca_y2026 > 0 ? 'actif' : (f.ca_global > 0 ? 'inactif' : 'nouveau'))
        if (String(id).indexOf('u-') === 0) {
          this.fromUser = f.utilisateur_id
          this.prospectId = null
        } else {
          this.prospectId = f.prospect_id || f.id
        }
      } catch (e) {
        this.msg = e.message || 'Chargement impossible'
      }
    },
    async ensureProspect() {
      const vars = {
        ...this.form,
        origin: this.origin,
        relation: this.known ? (this.origin === 'actif' ? 'client' : 'prospect') : 'inconnu',
        status: 'stand'
      }
      if (this.prospectId && String(this.prospectId).indexOf('u-') !== 0) {
        vars.id = String(this.prospectId)
      }
      if (this.fromUser) {
        vars.from_utilisateur_id = Number(this.fromUser)
      }
      const { data } = await this.$apollo.mutate({
        client: 'AUTH',
        mutation: require('src/graphql/auth/crmSalon/crmSalonUpsertProspect.gql'),
        variables: vars
      })
      this.prospectId = data.crmSalonUpsertProspect.id
      await this.maybeUploadPhoto()
      return this.prospectId
    },
    async maybeUploadPhoto() {
      const dataUrl = sessionStorage.getItem('crm_salon_photo_dataurl')
      if (!dataUrl || !this.prospectId) return
      try {
        const blob = await (await fetch(dataUrl)).blob()
        const formData = new FormData()
        formData.append('prospect_id', this.prospectId)
        formData.append('image', blob, 'carte.jpg')
        const token = localStorage.getItem('token_jwt')
        await this.axios.post('https://api-auth-plf.wee-do-it.net/api/v1/crm-salon/upload-carte', formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        sessionStorage.removeItem('crm_salon_photo_dataurl')
      } catch (e) {
        // non-bloquant
      }
    },
    async saveVisit() {
      this.saving = true
      this.msg = ''
      try {
        const id = await this.ensureProspect()
        const duration = Math.round((Date.now() - this.startedAt) / 1000)
        await this.$apollo.mutate({
          client: 'AUTH',
          mutation: require('src/graphql/auth/crmSalon/crmSalonLogVisit.gql'),
          variables: {
            prospect_id: String(id),
            salon: this.salon,
            salon_event_id: this.salonEventId || null,
            passage: this.passage,
            note_interne: this.noteInterne,
            duration_sec: duration,
            origin: this.origin,
            known: this.known,
            rdv: this.passage === 'rdv'
          }
        })
        this.msg = 'Visite enregistrée.'
        invalidateJourneeCache()
        this.$router.push(withFrom('/crm-salon/fiche/' + id, this.$route.fullPath))
      } catch (e) {
        this.msg = e.message || 'Erreur'
      } finally {
        this.saving = false
      }
    },
    async goQualify() {
      try {
        const id = await this.ensureProspect()
        this.$router.push(withFrom(
          { path: '/crm-salon/qualifier', query: { id } },
          this.$route.fullPath
        ))
      } catch (e) {
        this.msg = e.message || 'Erreur'
      }
    },
    goFiche() {
      if (this.prospectId) {
        this.$router.push(withFrom('/crm-salon/fiche/' + this.prospectId, this.$route.fullPath))
      }
    }
  }
}
</script>
